import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Plus } from 'lucide-react';
import { TaskTypeConfigSection, buildTaskTypesMap } from '../shared';
import { decoupledSave } from '../../services/api';
import { taskTypeCollectionProcessor } from '../../dataProcessors/decoupledApiProcessors';
import { createWorkflow, fetchWorkflowList, saveTaskTypeWorkflow } from '../workflow/workflowApi';
import WorkflowViewModal from '../workflow/WorkflowViewModal';

const TASK_TYPES_URL = '/decoupled-api/taxonomy_term/task_type/collection?display=decoupled';

// ---------------------------------------------------------------------------
// CreateWorkflowModal — modal à deux onglets (créer / assigner un flux existant)
// ---------------------------------------------------------------------------

const Spinner = () => (
  <svg className="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
  </svg>
);

const CreateWorkflowModal = ({ typeName, defaultId, loading, error, onConfirm, onAssign, onCancel, initialTab = 'create' }) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [workflowId, setWorkflowId] = useState(defaultId || '');
  const [label, setLabel] = useState(`Workflow ${typeName}`);
  const [availableWorkflows, setAvailableWorkflows] = useState([]);
  const [workflowsLoading, setWorkflowsLoading] = useState(false);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState('');

  useEffect(() => {
    if (activeTab === 'assign' && availableWorkflows.length === 0 && !workflowsLoading) {
      setWorkflowsLoading(true);
      fetchWorkflowList()
        .then(list => setAvailableWorkflows(list.filter(w => w.type === 'orbit_task')))
        .catch(() => setAvailableWorkflows([]))
        .finally(() => setWorkflowsLoading(false));
    }
  }, [activeTab]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="px-6 pt-6 pb-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Flux de travail — <span className="text-iris-700">{typeName}</span>
          </h3>
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setActiveTab('create')}
              className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-colors ${activeTab === 'create' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Créer un nouveau flux
            </button>
            <button
              onClick={() => setActiveTab('assign')}
              className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-colors ${activeTab === 'assign' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Utiliser un flux existant
            </button>
          </div>
        </div>

        <div className="px-6 py-4">
          {activeTab === 'create' && (
            <>
              <p className="text-sm text-gray-500 mb-4">
                Créez un nouveau flux pour définir les états et transitions de ce type de tâche.
              </p>
              <div className="space-y-3 mb-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Libellé</label>
                  <input
                    type="text"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    className="input"
                    placeholder="Libellé du workflow"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Identifiant machine</label>
                  <input
                    type="text"
                    value={workflowId}
                    onChange={(e) => setWorkflowId(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_').slice(0, 64))}
                    className="input font-mono"
                    placeholder="task_type_..."
                  />
                  <p className="text-xs text-gray-400 mt-1">Doit commencer par <code>task_type_</code>. Non modifiable après création.</p>
                </div>
              </div>
              {error && <p className="text-sm text-red-600 mb-3 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</p>}
              <div className="flex justify-end gap-2">
                <button onClick={onCancel} disabled={loading} className="btn btn-secondary">Annuler</button>
                <button onClick={() => onConfirm(workflowId, label)} disabled={loading || !workflowId.trim()} className="btn btn-primary flex items-center gap-2">
                  {loading && <Spinner />}
                  Créer le flux
                </button>
              </div>
            </>
          )}

          {activeTab === 'assign' && (
            <>
              <p className="text-sm text-gray-500 mb-4">
                Sélectionnez un flux de type <strong>orbit_task</strong> existant à assigner à ce type de tâche.
              </p>
              {workflowsLoading ? (
                <div className="flex items-center justify-center py-8 text-gray-400 gap-2"><Spinner /> Chargement…</div>
              ) : availableWorkflows.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-8">Aucun flux orbit_task disponible.</p>
              ) : (
                <div className="space-y-2 mb-4 max-h-60 overflow-y-auto pr-1">
                  {availableWorkflows.map(wf => (
                    <label
                      key={wf.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${selectedWorkflowId === wf.id ? 'border-iris-400 bg-iris-50' : 'border-slate-200 hover:border-iris-300 hover:bg-iris-50/50'}`}
                    >
                      <input type="radio" name="existing-workflow" value={wf.id} checked={selectedWorkflowId === wf.id} onChange={() => setSelectedWorkflowId(wf.id)} className="accent-iris-600" />
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-medium text-gray-800">{wf.label}</span>
                        <span className="block text-xs text-gray-400 font-mono">{wf.id}</span>
                      </span>
                    </label>
                  ))}
                </div>
              )}
              {error && <p className="text-sm text-red-600 mb-3 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</p>}
              <div className="flex justify-end gap-2">
                <button onClick={onCancel} disabled={loading} className="btn btn-secondary">Annuler</button>
                <button onClick={() => onAssign(selectedWorkflowId)} disabled={loading || !selectedWorkflowId} className="btn btn-primary flex items-center gap-2">
                  {loading && <Spinner />}
                  Assigner ce flux
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// TaskTypeFormModal — création d'un type de tâche ou d'un sous-type
//
// Un seul formulaire pour les deux cas : `parentName` à null crée un type
// racine, sinon un sous-type rattaché à `parentName`.
// ---------------------------------------------------------------------------

const TaskTypeFormModal = ({ parentName = null, existingNames = [], loading, error, onConfirm, onCancel }) => {
  const [name, setName] = useState('');
  const inputRef = useRef(null);
  const titleId = 'task-type-form-title';

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const trimmed = name.trim();
  const isDuplicate = existingNames.some(
    (existing) => (existing || '').trim().toLowerCase() === trimmed.toLowerCase()
  );
  const canSubmit = !!trimmed && !isDuplicate && !loading;

  const submit = (event) => {
    event.preventDefault();
    if (canSubmit) onConfirm(trimmed);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onKeyDown={(e) => { if (e.key === 'Escape' && !loading) onCancel(); }}
    >
      <div role="dialog" aria-modal="true" aria-labelledby={titleId} className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <form onSubmit={submit}>
          <div className="px-6 pt-6 pb-4 border-b border-gray-100">
            <h3 id={titleId} className="text-lg font-semibold text-gray-900">
              {parentName ? (
                <>Nouveau sous-type — <span className="text-iris-700">{parentName}</span></>
              ) : (
                'Nouveau type de tâche'
              )}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {parentName
                ? `Le sous-type sera rattaché au type « ${parentName} ».`
                : 'Le type sera disponible dans tous les projets une fois activé.'}
            </p>
          </div>

          <div className="px-6 py-4">
            <label htmlFor="task-type-name" className="block text-xs font-medium text-gray-700 mb-1">
              Libellé
            </label>
            <input
              id="task-type-name"
              ref={inputRef}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={`input ${isDuplicate ? 'input-error' : ''}`}
              placeholder={parentName ? 'Nom du sous-type' : 'Nom du type de tâche'}
              aria-describedby={isDuplicate ? 'task-type-name-error' : undefined}
              aria-invalid={isDuplicate || undefined}
            />
            {isDuplicate && (
              <p id="task-type-name-error" className="text-xs text-rose-600 mt-1">
                Ce nom existe déjà à ce niveau.
              </p>
            )}
            {error && (
              <p role="alert" className="text-sm text-rose-700 mt-3 bg-rose-50 border border-rose-200 rounded px-3 py-2">{error}</p>
            )}
            <div className="flex justify-end gap-2 mt-4">
              <button type="button" onClick={onCancel} disabled={loading} className="btn btn-secondary">Annuler</button>
              <button type="submit" disabled={!canSubmit} className="btn btn-primary flex items-center gap-2">
                {loading && <Spinner />}
                Créer
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// DataSourceBadge — indicateur local/backend
// ---------------------------------------------------------------------------

const DataSourceBadge = ({ isBackend }) => (
  <span className={`badge ${isBackend ? 'badge-success' : 'badge-warning'}`}>
    <span className={`w-1.5 h-1.5 rounded-full ${isBackend ? 'bg-green-500' : 'bg-amber-500'}`} />
    {isBackend ? 'Données backend' : 'Données locales'}
  </span>
);

// ---------------------------------------------------------------------------
// Utilitaire
// ---------------------------------------------------------------------------

const toMachineId = (str) =>
  (str || '')
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 55);

// ---------------------------------------------------------------------------
// TaskTypesPanel — onglet "Types de tâches" autonome
//
// Possède son propre cycle de chargement des données. Après chaque opération
// sur les workflows (créer, assigner, retirer), refreshData() recharge la liste
// localement sans toucher au contexte ReactiveJSON parent.
//
// Props :
//   defaultEnabledTypes   — valeur initiale des types activés par défaut
//   onEnabledTypesChange  — (typeName) => void : bascule un type
//   onSaveEnabledTypes    — () => void : persiste les types activés
//   showToast             — (message, type?) => void : toast partagé avec la page
// ---------------------------------------------------------------------------

const TaskTypesPanel = ({ defaultEnabledTypeIds = [], onSaveEnabledTypes, showToast }) => {
  const [taskTypes, setTaskTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  // Name-keyed enabled map for the toggles. Owned here so the toggle UI and the
  // term-id resolution at save time share ONE source of truth (this panel's
  // fetched task types), avoiding any divergence with the parent page.
  const [enabledMap, setEnabledMap] = useState({});

  // Workflow modal state
  const [showCreateWorkflowModal, setShowCreateWorkflowModal] = useState(false);
  const [createWorkflowInitialTab, setCreateWorkflowInitialTab] = useState('create');
  const [pendingWorkflowCreation, setPendingWorkflowCreation] = useState(null);
  const [createWorkflowLoading, setCreateWorkflowLoading] = useState(false);
  const [createWorkflowError, setCreateWorkflowError] = useState(null);
  const [viewWorkflowModal, setViewWorkflowModal] = useState({ isOpen: false, workflowId: null, workflowLabel: null, typeName: null, tid: null });

  // Create type / subtype modal state. `parentName` null => top-level type.
  const [typeForm, setTypeForm] = useState(null);
  const [typeFormLoading, setTypeFormLoading] = useState(false);
  const [typeFormError, setTypeFormError] = useState(null);

  // ---------------------------------------------------------------------------
  // Data
  // ---------------------------------------------------------------------------

  useEffect(() => {
    setLoading(true);
    // Cache-busting timestamp ensures the request bypasses the LRU cache
    // (requestCache.js caches all /decoupled-api/ GETs by URL).
    const url = `${TASK_TYPES_URL}&_=${Date.now()}`;
    fetch(url, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(raw => {
        if (!raw) return;
        setTaskTypes(taskTypeCollectionProcessor({
          requestContext: { url },
          responseContext: { status: 200 },
          dataToProcess: raw,
        }));
      })
      .finally(() => setLoading(false));
  }, [refreshKey]);

  const { meta: typeMeta } = useMemo(
    () => buildTaskTypesMap(taskTypes.length > 0 ? taskTypes : null),
    [taskTypes]
  );

  const hasBackendData = taskTypes.length > 0;

  // tid -> name for the loaded top-level types (defaults arrive as term ids).
  const tidToName = useMemo(() => {
    const m = {};
    Object.entries(typeMeta || {}).forEach(([name, meta]) => {
      if (meta?.tid != null) m[String(meta.tid)] = name;
    });
    return m;
  }, [typeMeta]);

  // Seed the toggle map once the task types have loaded (term ids -> names).
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current || Object.keys(tidToName).length === 0) return;
    const map = {};
    (Array.isArray(defaultEnabledTypeIds) ? defaultEnabledTypeIds : []).forEach((tid) => {
      const name = tidToName[String(tid)];
      if (name) map[name] = true;
    });
    setEnabledMap(map);
    seeded.current = true;
  }, [tidToName, defaultEnabledTypeIds]);

  const handleToggle = (typeName) =>
    setEnabledMap((prev) => ({ ...prev, [typeName]: !prev[typeName] }));

  const handleSave = () => {
    const tids = Object.entries(enabledMap)
      .filter(([, on]) => on)
      .map(([name]) => typeMeta[name]?.tid)
      .filter((tid) => tid != null)
      .map(Number);
    // Guard: if types are enabled in the UI but none resolved to an id, the
    // type map failed to load — abort rather than silently wipe the defaults.
    if (Object.values(enabledMap).some(Boolean) && tids.length === 0) {
      showToast('Types de tâches non chargés — réessayez', 'error');
      return;
    }
    onSaveEnabledTypes(tids);
  };

  // ---------------------------------------------------------------------------
  // Create type / subtype handlers
  //
  // A task type is a task_type taxonomy term; a subtype is the same term with a
  // `parent` — the very shape buildTaskTypesMap already reads back. Both go
  // through the decoupled_write display, which exposes name + parent for write.
  // ---------------------------------------------------------------------------

  const openCreateType = () => {
    setTypeFormError(null);
    setTypeForm({ parentName: null, parentTid: null, siblings: Object.keys(typeMeta || {}) });
  };

  const openCreateSubtype = (typeName, meta, subtypes) => {
    setTypeFormError(null);
    setTypeForm({ parentName: typeName, parentTid: meta?.tid ?? null, siblings: subtypes ?? [] });
  };

  const closeTypeForm = () => {
    setTypeForm(null);
    setTypeFormError(null);
  };

  const handleConfirmCreateType = async (name) => {
    if (!typeForm) return;
    const { parentName, parentTid } = typeForm;
    if (parentName && !parentTid) {
      setTypeFormError("Type parent introuvable — rechargez la page et réessayez.");
      return;
    }
    setTypeFormLoading(true);
    setTypeFormError(null);
    try {
      const data = { name };
      if (parentTid) data.parent = Number(parentTid);
      await decoupledSave('taxonomy_term', 'task_type', data, null, { display: 'decoupled_write' });
      closeTypeForm();
      showToast(parentName ? `Sous-type « ${name} » créé` : `Type de tâche « ${name} » créé`);
      setRefreshKey(k => k + 1);
    } catch (err) {
      setTypeFormError(err.message || 'Erreur lors de la création');
    } finally {
      setTypeFormLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Workflow handlers
  // ---------------------------------------------------------------------------

  const handleOpenWorkflow = (typeName, meta) => {
    const resolvedMeta = meta ?? typeMeta[typeName] ?? {};
    if (resolvedMeta.workflowId) {
      setViewWorkflowModal({ isOpen: true, workflowId: resolvedMeta.workflowId, workflowLabel: typeName, typeName, tid: resolvedMeta.tid });
    } else {
      const suggestedId = 'task_type_' + toMachineId(typeName);
      setPendingWorkflowCreation({ typeName, tid: resolvedMeta.tid, suggestedId });
      setCreateWorkflowInitialTab('create');
      setCreateWorkflowError(null);
      setShowCreateWorkflowModal(true);
    }
  };

  const handleReassignWorkflow = () => {
    const { typeName, tid } = viewWorkflowModal;
    setPendingWorkflowCreation({ typeName, tid, suggestedId: 'task_type_' + toMachineId(typeName || '') });
    setCreateWorkflowInitialTab('assign');
    setCreateWorkflowError(null);
    setShowCreateWorkflowModal(true);
  };

  const closeCreateModal = () => {
    setShowCreateWorkflowModal(false);
    setPendingWorkflowCreation(null);
    setCreateWorkflowError(null);
    setCreateWorkflowInitialTab('create');
  };

  const handleConfirmCreateWorkflow = async (workflowId, label) => {
    if (!pendingWorkflowCreation) return;
    setCreateWorkflowLoading(true);
    setCreateWorkflowError(null);
    try {
      await createWorkflow(workflowId, label, pendingWorkflowCreation.tid);
      closeCreateModal();
      showToast('Flux créé avec succès');
      setRefreshKey(k => k + 1);
    } catch (err) {
      setCreateWorkflowError(err.message || 'Erreur lors de la création du flux');
    } finally {
      setCreateWorkflowLoading(false);
    }
  };

  const handleConfirmAssignWorkflow = async (selectedWorkflowId) => {
    if (!pendingWorkflowCreation || !selectedWorkflowId) return;
    setCreateWorkflowLoading(true);
    setCreateWorkflowError(null);
    try {
      await saveTaskTypeWorkflow(pendingWorkflowCreation.tid, selectedWorkflowId);
      closeCreateModal();
      showToast('Flux assigné avec succès');
      setRefreshKey(k => k + 1);
    } catch (err) {
      setCreateWorkflowError(err.message || "Erreur lors de l'assignation du flux");
    } finally {
      setCreateWorkflowLoading(false);
    }
  };

  const handleUnassignWorkflow = async () => {
    const { tid, typeName } = viewWorkflowModal;
    if (!tid) return;
    try {
      await saveTaskTypeWorkflow(tid, null);
      setViewWorkflowModal({ isOpen: false, workflowId: null, workflowLabel: null, typeName: null, tid: null });
      showToast(`Flux retiré de « ${typeName} »`, 'info');
      setRefreshKey(k => k + 1);
    } catch (err) {
      showToast(err.message || 'Erreur lors du retrait du flux', 'error');
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-bold text-gray-900">Types de tâches par défaut</h3>
          <p className="text-sm text-gray-500 mt-1">
            Définissez les types de tâches activés par défaut pour les nouveaux projets
            <span className="ml-2">
              <DataSourceBadge isBackend={hasBackendData} />
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={openCreateType}
            className="btn btn-secondary"
          >
            <Plus size={16} aria-hidden="true" />
            Nouveau type de tâche
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="btn btn-primary"
          >
            Enregistrer
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6">
        {loading ? (
          <div className="flex items-center justify-center py-12 text-gray-400 gap-2">
            <Spinner /> Chargement des types de tâches…
          </div>
        ) : (
          <TaskTypeConfigSection
            taskTypes={hasBackendData ? taskTypes : null}
            enabledTypes={enabledMap}
            onChange={handleToggle}
            onWorkflowClick={handleOpenWorkflow}
            onAddSubtype={openCreateSubtype}
          />
        )}
      </div>

      {/* Create Task Type / Subtype Modal */}
      {typeForm && (
        <TaskTypeFormModal
          parentName={typeForm.parentName}
          existingNames={typeForm.siblings}
          loading={typeFormLoading}
          error={typeFormError}
          onConfirm={handleConfirmCreateType}
          onCancel={closeTypeForm}
        />
      )}

      {/* Create / Assign Workflow Modal */}
      {showCreateWorkflowModal && pendingWorkflowCreation && (
        <CreateWorkflowModal
          typeName={pendingWorkflowCreation.typeName}
          defaultId={pendingWorkflowCreation.suggestedId}
          loading={createWorkflowLoading}
          error={createWorkflowError}
          initialTab={createWorkflowInitialTab}
          onConfirm={handleConfirmCreateWorkflow}
          onAssign={handleConfirmAssignWorkflow}
          onCancel={closeCreateModal}
        />
      )}

      {/* View Workflow Modal (read-only) */}
      <WorkflowViewModal
        isOpen={viewWorkflowModal.isOpen}
        onClose={() => setViewWorkflowModal({ isOpen: false, workflowId: null, workflowLabel: null, typeName: null, tid: null })}
        workflowId={viewWorkflowModal.workflowId}
        workflowLabel={viewWorkflowModal.workflowLabel}
        canEdit={true}
        onReassign={handleReassignWorkflow}
        onUnassign={handleUnassignWorkflow}
      />
    </div>
  );
};

export default TaskTypesPanel;
